import {
  Badge,
  Button,
  Checkbox,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { User } from "lucide-react";
import { useLayoutEffect, useRef } from "react";
import type { FeedCommentRow } from "@/modules/cf-feed/presentation/api/types";
import { getMessengerDeliveryStatusPresentation } from "@/modules/cf-feed/presentation/lib/messenger-delivery-status";
import { useBoundedInfiniteScroll } from "@/modules/cf-feed/presentation/lib/use-bounded-infinite-scroll";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { AppImage } from "@/shared/ui/AppImage";

const keepInfiniteRowsVisible = () => undefined;

export type FeedCommentsTableProps = {
  data: FeedCommentRow[];
  isLoading?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  selectedIds: Set<string>;
  onSelectionChange: (selectedIds: Set<string>) => void;
  onCreateSelected: () => void;
  onCreateOrder: (comment: FeedCommentRow) => void;
  onLinkMessenger: (comment: FeedCommentRow) => void;
  selectionLimit?: number;
};

function CommenterCell({ row }: { row: FeedCommentRow }) {
  const content = (
    <span className="inline-flex min-w-0 items-center gap-2">
      <span className="size-8 shrink-0 overflow-hidden rounded-full border bg-muted">
        <AppImage
          src={row.commenterPictureUrl}
          alt={row.commenterName}
          className="size-full"
          showLoading={false}
          fallback={
            <div className="flex size-full items-center justify-center text-muted-foreground">
              <User className="size-4" />
            </div>
          }
        />
      </span>
      <span className="min-w-0 truncate text-sm">{row.commenterName}</span>
    </span>
  );

  if (row.profileLink) {
    return (
      <a
        href={row.profileLink}
        target="_blank"
        rel="noreferrer"
        className="text-foreground hover:underline"
        title={row.profileLink}
      >
        {content}
      </a>
    );
  }
  return content;
}

function MessengerStatusCell({ row }: { row: FeedCommentRow }) {
  if (row.messengerStatus === "not_applicable") {
    return row.matchResult === "message_pending" ? (
      <Badge variant="outline">ລໍຖ້າຂໍ້ຄວາມຢືນຢັນ</Badge>
    ) : (
      <Badge variant="secondary">ຍັງບໍ່ມີອໍເດີ້</Badge>
    );
  }

  const presentation = getMessengerDeliveryStatusPresentation({
    status: row.messengerStatus,
    errorCode: row.messengerErrorCode,
  });
  return (
    <div className="space-y-1">
      <Badge variant={presentation.variant}>{presentation.label}</Badge>
      {presentation.recoveryHint ? (
        <p className="max-w-56 text-muted-foreground text-xs leading-snug">
          {presentation.recoveryHint}
        </p>
      ) : null}
    </div>
  );
}

export function FeedCommentsTable({
  data,
  isLoading,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  selectedIds,
  onSelectionChange,
  onCreateSelected,
  onCreateOrder,
  onLinkMessenger,
  selectionLimit = 100,
}: FeedCommentsTableProps) {
  const { scrollRootRef, sentinelRef } = useBoundedInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
  });
  const scrollSnapshotRef = useRef<{
    firstId: string | null;
    rowCount: number;
    scrollHeight: number;
    scrollTop: number;
  } | null>(null);

  useLayoutEffect(() => {
    const root = scrollRootRef.current;
    if (!root) return;
    const previous = scrollSnapshotRef.current;
    const firstId = data[0]?.id ?? null;
    if (
      previous &&
      previous.firstId !== firstId &&
      data.length > previous.rowCount &&
      previous.scrollTop > 24
    ) {
      const addedHeight = root.scrollHeight - previous.scrollHeight;
      if (addedHeight > 0) {
        root.scrollTop = previous.scrollTop + addedHeight;
      }
    }
    scrollSnapshotRef.current = {
      firstId,
      rowCount: data.length,
      scrollHeight: root.scrollHeight,
      scrollTop: root.scrollTop,
    };
  }, [data, scrollRootRef]);
  const selectableIds = data
    .filter((comment) => !comment.hasOrder)
    .map((comment) => comment.id);

  const columns: TanstackReactTable.ColumnDef<FeedCommentRow>[] = [
    {
      id: "select",
      header: () => {
        if (selectableIds.length === 0) return null;
        const allChecked = selectableIds.every((id) => selectedIds.has(id));
        const someChecked = selectableIds.some((id) => selectedIds.has(id));
        return (
          <Checkbox
            aria-label="ເລືອກຄອມເມັນທັງໝົດໃນໜ້ານີ້"
            checked={allChecked ? true : someChecked ? "indeterminate" : false}
            onCheckedChange={(value) => {
              const next = new Set(selectedIds);
              if (value) {
                for (const id of selectableIds) {
                  if (next.size >= selectionLimit) break;
                  next.add(id);
                }
              } else {
                for (const id of selectableIds) next.delete(id);
              }
              onSelectionChange(next);
            }}
          />
        );
      },
      cell: ({ row }) =>
        row.original.hasOrder ? null : (
          <Checkbox
            aria-label={`ເລືອກຄອມເມັນຂອງ ${row.original.commenterName}`}
            checked={selectedIds.has(row.original.id)}
            disabled={
              !selectedIds.has(row.original.id) &&
              selectedIds.size >= selectionLimit
            }
            onCheckedChange={(value) => {
              const next = new Set(selectedIds);
              if (value) next.add(row.original.id);
              else next.delete(row.original.id);
              onSelectionChange(next);
            }}
          />
        ),
      size: 40,
    },
    {
      id: "index",
      header: "ລຳດັບ",
      cell: ({ row }) => row.original.index,
      size: 64,
    },
    {
      id: "commenter",
      header: "ຊື່",
      cell: ({ row }) => <CommenterCell row={row.original} />,
      size: 220,
    },
    {
      id: "text",
      header: "ຄອມເມັນ",
      cell: ({ row }) => (
        <p className="line-clamp-2 max-w-md">{row.original.commentText}</p>
      ),
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => <MessengerStatusCell row={row.original} />,
      size: 250,
    },
    {
      id: "time",
      header: "ເວລາ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-xs">
          {formatDateTimeLocal(
            row.original.facebookCreatedTime ?? row.original.receivedAt,
          )}
        </span>
      ),
      size: 160,
    },
    {
      id: "actions",
      header: "ຈັດການ",
      cell: ({ row }) =>
        row.original.canLinkMessenger ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onLinkMessenger(row.original)}
          >
            ເຊື່ອມ Messenger
          </Button>
        ) : row.original.hasOrder ? null : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onCreateOrder(row.original)}
          >
            ສ້າງອໍເດີ້
          </Button>
        ),
      size: 110,
    },
  ];

  return (
    <div className="space-y-3">
      {selectedIds.size > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
          <p className="text-sm">
            ເລືອກແລ້ວ <strong>{selectedIds.size}</strong> ຄອມເມັນ
            <span className="text-muted-foreground"> / {selectionLimit}</span>
          </p>
          <Button type="button" size="sm" onClick={onCreateSelected}>
            ສ້າງອໍເດີ້ທີ່ເລືອກ
          </Button>
        </div>
      ) : null}
      <div
        ref={scrollRootRef}
        onScroll={(event) => {
          const root = event.currentTarget;
          scrollSnapshotRef.current = {
            firstId: data[0]?.id ?? null,
            rowCount: data.length,
            scrollHeight: root.scrollHeight,
            scrollTop: root.scrollTop,
          };
        }}
        className="max-h-[36rem] overflow-auto overscroll-contain rounded-md [overflow-anchor:none]"
      >
        <DataTable<FeedCommentRow, unknown>
          data={data}
          columns={columns}
          isLoading={isLoading}
          noDataMessage="ຍັງບໍ່ມີຄອມເມັນ"
          enablePagination={false}
          onPaginationChange={keepInfiniteRowsVisible}
          limit={Math.max(data.length, 1)}
          offset={0}
          totalCount={data.length}
        />
        {hasNextPage ? (
          <div
            ref={sentinelRef}
            className="flex min-h-10 items-center justify-center text-muted-foreground text-xs"
            aria-live="polite"
          >
            {isFetchingNextPage ? "ກຳລັງໂຫຼດເພີ່ມ..." : "ເລື່ອນເພື່ອໂຫຼດເພີ່ມ"}
          </div>
        ) : null}
      </div>
    </div>
  );
}
